import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    IconButton,
    Stack,
    Typography,
    Tooltip,
    Avatar,
    Badge,
    Popover,
    List,
    ListItem,
    ListItemAvatar,
    CircularProgress,
    Button,
    Divider
} from '@mui/material';
import LogoImg from '../../assests/logo.png';
import MenuDrawer from './components/menu-dropdown';
import PersonIcon from '@mui/icons-material/Person';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarningIcon from '@mui/icons-material/Warning';
import EmailIcon from '@mui/icons-material/Email';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoIcon from '@mui/icons-material/Info';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function formatRelativeTime(dateString) {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (isNaN(date.getTime())) return '';
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
}

const getNotificationStyles = (type) => {
    switch (type) {
        case 'low_stock':
            return {
                icon: <WarningIcon sx={{ color: '#E65100' }} />,
                bg: '#FFE0B2',
                label: 'Shortage'
            };
        case 'new_order':
            return {
                icon: <ShoppingCartIcon sx={{ color: '#2E7D32' }} />,
                bg: '#C8E6C9',
                label: 'New Order'
            };
        case 'refund_alert':
            return {
                icon: <PaymentIcon sx={{ color: '#C62828' }} />,
                bg: '#FFCDD2',
                label: 'Refund Needed'
            };
        case 'supplier_reply':
            return {
                icon: <EmailIcon sx={{ color: '#1565C0' }} />,
                bg: '#BBDEFB',
                label: 'Supplier Reply'
            };
        default:
            return {
                icon: <InfoIcon sx={{ color: '#37474F' }} />,
                bg: '#CFD8DC',
                label: 'Update'
            };
    }
};

function TopNavbar({ isLoggedIn }) {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    
    // Notifications States
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [bellAnchorEl, setBellAnchorEl] = useState(null);
    const [importingId, setImportingId] = useState(null);
    const openNotifications = Boolean(bellAnchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleBellClick = (event) => {
        setBellAnchorEl(event.currentTarget);
        fetchNotifications(true);
    };

    const handleBellClose = () => {
        setBellAnchorEl(null);
    };

    const fetchNotifications = async (showLoading = false) => {
        const token = sessionStorage.getItem('adminToken');
        if (!token) return;
        
        if (showLoading) setLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000/api';
        try {
            const res = await axios.get(`${apiUrl}/notification`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data?.data) {
                const list = res.data.data;
                setNotifications(list);
                const unread = list.filter(n => !n.read).length;
                setUnreadCount(unread);
            }
        } catch (err) {
            console.error("Error fetching notifications:", err);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const handleMarkAsRead = async (id, event) => {
        if (event) event.stopPropagation();
        const token = sessionStorage.getItem('adminToken');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000/api';
        try {
            await axios.put(`${apiUrl}/notification/${id}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(prev => prev.filter(n => n.id !== id));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error("Failed to mark notification as read:", err);
        }
    };

    const handleMarkAllRead = async () => {
        const token = sessionStorage.getItem('adminToken');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000/api';
        try {
            await axios.put(`${apiUrl}/notification/read-all`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications([]);
            setUnreadCount(0);
        } catch (err) {
            console.error("Failed to mark all as read:", err);
        }
    };

    const handleImportStock = async (id, event) => {
        if (event) event.stopPropagation();
        const token = sessionStorage.getItem('adminToken');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000/api';
        setImportingId(id);
        try {
            const res = await axios.post(`${apiUrl}/notification/${id}/import`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const { updatedCount, errors } = res.data?.data || {};
            alert(`Stock imported successfully!\nUpdated Products: ${updatedCount || 0}${errors?.length ? `\nErrors:\n${errors.join('\n')}` : ''}`);
            fetchNotifications(false);
        } catch (err) {
            alert("Failed to import stock: " + (err.response?.data?.message || err.message));
        } finally {
            setImportingId(null);
        }
    };

    const handleDownloadExcel = (attachmentPath, event) => {
        if (event) event.stopPropagation();
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000/api';
        const serverUrl = apiUrl.replace('/api', '');
        const downloadUrl = `${serverUrl}/${attachmentPath}`;
        window.open(downloadUrl, '_blank');
    };

const handleNotificationClick = async (item) => {
        if (!item.read) {
            await handleMarkAsRead(item.id);
        }
        
        handleBellClose();

        if (['new_order', 'refund_alert', 'order_status_change'].includes(item.type) && item.referenceId) {
            if (item.type === 'refund_alert') {
                navigate('/order?status=failed_refundable');
            } else {
                navigate(`/order/${item.referenceId}`);
            }
        } else if (item.type === 'low_stock' || item.type === 'supplier_reply') {
            navigate('/inventory');
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        if (isLoggedIn === 'true' || isLoggedIn === true) {
            fetchNotifications(false);
            const interval = setInterval(() => {
                fetchNotifications(false);
            }, 45000);
            return () => clearInterval(interval);
        }
    }, [isLoggedIn]);

    const handleScroll = (event) => {
        event.stopPropagation();
    };

    return (
        <Box sx={{
            display: 'block',
            py: 1,
            borderBottom: '1px solid lightGray',
            bgcolor: '#FAFAF9',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1301,
            width: 1,
            pr: 2,
        }}
            onScroll={handleScroll}
        >
            <Container maxWidth="xlg">
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <IconButton sx={{ borderRadius: '10px', px: 2 }} onClick={() => navigate("/property")}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <img
                                src={LogoImg}
                                alt="Login Logo"
                                width={20}
                            />
                            <Typography
                                ml={1}
                                color={'black'}
                                fontSize={14}
                                fontWeight={500}
                            >Bhabhuti</Typography>
                        </Box>
                    </IconButton>
                    <Stack
                        direction={'row'}
                        gap={2}
                        alignItems={'center'}
                        display={isLoggedIn ? 'flex' : 'none'}
                    >
                        <Typography
                            fontWeight={500}
                            fontSize={14}
                            sx={{
                                '&:hover': {
                                    color: 'black',
                                    cursor: 'pointer'
                                }
                            }}
                            onClick={() => navigate("/")}
                        >Dashboard</Typography>

                        {/* Notification Bell Icon */}
                        <IconButton
                            onClick={handleBellClick}
                            size="small"
                            sx={{ color: 'text.secondary' }}
                        >
                            <Badge badgeContent={unreadCount} color="error">
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>

                        <Popover
                            open={openNotifications}
                            anchorEl={bellAnchorEl}
                            onClose={handleBellClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            PaperProps={{
                                sx: {
                                    width: 360,
                                    maxHeight: 480,
                                    boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.1)',
                                    borderRadius: '12px',
                                    mt: 1.5,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }
                            }}
                        >
                            {/* Popover Header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, bgcolor: '#F5F5F4' }}>
                                <Typography fontWeight={700} fontSize={14} color="black">
                                    Notifications ({unreadCount})
                                </Typography>
                                {unreadCount > 0 && (
                                    <Button size="small" onClick={handleMarkAllRead} sx={{ fontSize: 11, fontWeight: 600, textTransform: 'none' }}>
                                        Mark all read
                                    </Button>
                                )}
                            </Box>
                            <Divider />

                            {/* Popover Body */}
                            <Box sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: 380 }}>
                                {loading && notifications.length === 0 ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : notifications.length === 0 ? (
                                    <Box sx={{ py: 6, px: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                        <Typography color="text.secondary" fontSize={13}>No new notifications</Typography>
                                    </Box>
                                ) : (
                                    <List disablePadding>
                                        {notifications.map((item) => {
                                            const styles = getNotificationStyles(item.type);
                                            return (
                                                <Box key={item.id}>
                                                    <ListItem
                                                        onClick={() => handleNotificationClick(item)}
                                                        sx={{
                                                            py: 1.5,
                                                            px: 2,
                                                            cursor: 'pointer',
                                                            bgcolor: item.read ? 'transparent' : 'rgba(25, 118, 210, 0.04)',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(0, 0, 0, 0.02)'
                                                            },
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'stretch'
                                                        }}
                                                    >
                                                        <Box display="flex" gap={1.5}>
                                                            <ListItemAvatar sx={{ minWidth: 40 }}>
                                                                <Avatar sx={{ bgcolor: styles.bg, width: 40, height: 40 }}>
                                                                    {styles.icon}
                                                                </Avatar>
                                                            </ListItemAvatar>
                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <Typography fontSize={13} fontWeight={item.read ? 600 : 700} color="black">
                                                                    {item.title}
                                                                </Typography>
                                                                <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                                                                    {item.message}
                                                                </Typography>
                                                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                                                    <Typography fontSize={10} color="text.disabled">
                                                                        {formatRelativeTime(item.createdAt)}
                                                                    </Typography>
                                                                    {!item.read && (
                                                                        <Button 
                                                                            size="small" 
                                                                            variant="text" 
                                                                            onClick={(e) => handleMarkAsRead(item.id, e)}
                                                                            sx={{ fontSize: 10, p: 0, minWidth: 0, textTransform: 'none', fontWeight: 600 }}
                                                                        >
                                                                            Mark read
                                                                        </Button>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Box>

                                                        {/* Action Buttons for Supplier Replies */}
                                                        {item.type === 'supplier_reply' && item.attachmentPath && (
                                                            <Box display="flex" gap={1.5} mt={1.5} ml={6}>
                                                                <Button 
                                                                    size="small" 
                                                                    variant="outlined" 
                                                                    startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                                                                    onClick={(e) => handleDownloadExcel(item.attachmentPath, e)}
                                                                    sx={{ fontSize: 11, py: 0.5, px: 1, textTransform: 'none', borderRadius: '6px' }}
                                                                >
                                                                    Download Sheet
                                                                </Button>
                                                                <Button 
                                                                    size="small" 
                                                                    variant="contained" 
                                                                    disabled={importingId === item.id}
                                                                    startIcon={importingId === item.id ? <CircularProgress size={12} /> : <UploadFileIcon sx={{ fontSize: 14 }} />}
                                                                    onClick={(e) => handleImportStock(item.id, e)}
                                                                    sx={{ fontSize: 11, py: 0.5, px: 1, textTransform: 'none', borderRadius: '6px' }}
                                                                >
                                                                    Import Stock
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </ListItem>
                                                    <Divider />
                                                </Box>
                                            );
                                        })}
                                    </List>
                                )}
                            </Box>
                        </Popover>

                        <Tooltip title="Account settings">
                            <IconButton
                                onClick={handleClick}
                                size="small"
                                sx={{ ml: 2, p: 0 }}
                                aria-controls={open ? 'account-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                            >
                                <Avatar sx={{ width: 32, height: 32 }}>
                                    <PersonIcon sx={{ width: '100%' }} />
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                        <MenuDrawer
                            open={open}
                            anchorEl={anchorEl}
                            handleClose={handleClose}
                        />
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
}

export default TopNavbar;